import { DespatchAttemptKind, DespatchAttemptOutcome, DespatchErrorBody, DespatchState, ReceiptAdviceState, ShipmentMode, ShipmentStatus as S, ShippingModePolicy } from "../types";
import { isValidTckn, isValidVkn } from "./identity";
import { poDate, poErr, poFmtDate, poFmtQty, poLayerBlocked, poNum } from "./purchasing";

/** Sevkiyat ekranının ortak yardımcıları (liste, çekmece, sevkiyat modalı, işlem/yazdırma pencereleri, OrderPicker). */
export const SH_STATUS_LABEL: Record<S, string> = {
  [S.Draft]: "Taslak",
  [S.Shipped]: "Sevk edildi",
  [S.Delivered]: "Teslim edildi",
  [S.Cancelled]: "İptal",
};
/** Mevcut .po-st tonlarına eşleme: gri / bilgi / yeşil / kırmızı. `s${status}` KULLANMA (3 = Delivered, po-st s3 değil s4). */
export const SH_STATUS_CLASS: Record<S, "s1" | "s2" | "s4" | "s5"> = {
  [S.Draft]: "s1",
  [S.Shipped]: "s2",
  [S.Delivered]: "s4",
  [S.Cancelled]: "s5",
};

// Eski dışa aktarımlar (OrderPicker satırları bunları kullanır)
export const fmtDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString("tr-TR") : "—");
export const fmtDateTime = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
export const fmtQty = (n: number) => n.toLocaleString("tr-TR", { maximumFractionDigits: 3 });

export const shFmtDate = poFmtDate;      // yalnız tarih ("12 Eyl 2026"): shipmentDate, waybillDate, orderDate, deadline, cancelledAt
export const shFmtDateTime = fmtDateTime; // gerçek anlar: shippedAt, deliveredAt, createdAt, updatedAt
export const shFmtQty = poFmtQty;
export const shErr = poErr;
export const shNum = poNum;
export const shLayerBlocked = poLayerBlocked;
export const shIsOpen = (s: S) => s === S.Draft || s === S.Shipped;

export const EPS = 0.0005;
/** Sunucu 3 basamağa yuvarlar; float gürültüsü girdiye sızmasın. Ondalık virgülle yazılır: "1.255" binlik sanılıp 1255 okunmasın. */
export const qtyText = (n: number) => String(Math.round(n * 1000) / 1000).replace(".", ",");

const QTY_PLAIN = /^(?:\d+(?:[.,]\d{1,3})?|[.,]\d{1,3})$/;   // 1250 · 1250,5 · 1250.5 · 0.125 · ,5
const QTY_TR = /^[1-9]\d{0,2}(?:\.\d{3})+(?:,\d{1,3})?$/;    // 1.250 · 1.250,5 (tr-TR binlik ayraçlı; "0.125" buraya düşmez)
export type QtyParse = { ok: true; value: number } | { ok: false; code: "empty" | "bad" | "zero" };
/** Miktar girdisi: sessizce kırpmaz, geçersizi reddeder. */
export const parseQty = (s: string): QtyParse => {
  const t = (s || "").trim();
  if (!t) return { ok: false, code: "empty" };
  let n: number;
  if (QTY_TR.test(t)) n = parseFloat(t.replace(/\./g, "").replace(",", "."));
  else if (QTY_PLAIN.test(t)) n = parseFloat(t.replace(",", "."));
  else return { ok: false, code: "bad" };
  if (!isFinite(n)) return { ok: false, code: "bad" };
  return n > EPS ? { ok: true, value: n } : { ok: false, code: "zero" };
};

/** Sevkiyat yöntemi etiketleri (liste, çekmece, yeni sevkiyat seçimi). */
export const SH_MODE_LABEL: Record<ShipmentMode, string> = {
  [ShipmentMode.Quantity]: "Miktarlı",
  [ShipmentMode.WholeOrder]: "Bütün sipariş",
};
/** Sistem ayarı seçenekleri — Sistem Ayarları > Sevkiyat. */
export const SH_POLICY_OPTIONS: { value: ShippingModePolicy; label: string; desc: string }[] = [
  { value: ShippingModePolicy.Both, label: "İkisi de", desc: "Her yeni sevkiyatta yöntem seçilir: tamamlanan siparişleri karekodla bütün olarak ya da miktar girerek." },
  { value: ShippingModePolicy.WholeOrder, label: "Siparişe üretim · tamamlanan sipariş bütün olarak", desc: "Yalnız durumu Tamamlandı olan siparişler sevk edilir; sipariş bölünmez, karekod okutularak eklenir." },
  { value: ShippingModePolicy.Quantity, label: "Stoğa üretim · miktar girerek", desc: "Firmanın siparişleri seçilip miktar yazılır; kısmi sevk serbest, sipariş durumu aranmaz." },
];
/** Basılı kısa sipariş no: GUID'in ilk 8 hanesi, büyük harf (sunucuda ShippingService.OrderNoOf). */
export const shOrderNo = (orderId: string) => orderId.replace(/-/g, "").slice(0, 8).toUpperCase();

/** Taşıma alanları — miktarlı formdaki kurallarla aynı (koli tam sayı, ağırlık ≥ 0, TCKN 11 hane, posta kodu 5 hane, taşıyıcı VKN 10/11 hane). Hata yoksa null. */
export const shTransportError = (h: { packageCount: string; grossWeightKg: string; driverTckn: string; deliveryPostalCode?: string; carrierTaxNumber?: string }):
  { field: "packageCount" | "grossWeightKg" | "driverTckn" | "deliveryPostalCode" | "carrierTaxNumber"; msg: string } | null => {
  if (h.packageCount.trim() !== "" && !/^\d+$/.test(h.packageCount.trim())) return { field: "packageCount", msg: "Koli sayısı tam sayı olmalı." };
  const gw = parseQty(h.grossWeightKg);
  if (h.grossWeightKg.trim() !== "" && !gw.ok && gw.code !== "zero") return { field: "grossWeightKg", msg: "Brüt ağırlık geçersiz (örn. 125,5); boş da bırakılabilir." };
  if (h.driverTckn.length !== 0 && h.driverTckn.length !== 11) return { field: "driverTckn", msg: "Şoför TCKN 11 haneli rakam olmalı." };
  const pc = (h.deliveryPostalCode || "").trim();
  if (pc && !/^\d{5}$/.test(pc)) return { field: "deliveryPostalCode", msg: "Teslimat posta kodu 5 haneli rakam olmalı." };
  const ct = (h.carrierTaxNumber || "").trim();
  if (ct && !/^\d{10,11}$/.test(ct)) return { field: "carrierTaxNumber", msg: "Taşıyıcı VKN 10, TCKN 11 haneli rakam olmalı." };
  return null;
};

/** Eski tek alan "Şoför" → ad / soyad (son boşluktan; sunucu DespatchRules.SplitName ile aynı). */
export const splitName = (full?: string | null): [string, string] => {
  const t = (full || "").trim().replace(/\s+/g, " ");
  if (!t) return ["", ""];
  const i = t.lastIndexOf(" ");
  return i < 0 ? [t, ""] : [t.slice(0, i), t.slice(i + 1)];
};
/** Şoför: ad/soyad ayrı alanlar; yalnız eski tek alan doluysa son boşluktan bölünür. */
export const shDriverNames = (d: { driverFirstName?: string | null; driverLastName?: string | null; driverName?: string | null }): [string, string] =>
  d.driverFirstName || d.driverLastName ? [d.driverFirstName || "", d.driverLastName || ""] : splitName(d.driverName);

// ======================= e-İrsaliye =======================

/** Platform irsaliye durumu → kısa Türkçe etiket ve rozet tonu (.po-badge ok/info/warn/err/muted). */
export const DESPATCH_STATE_LABEL: Record<DespatchState, string> = {
  Queued: "Gönderim bekliyor",
  SentToProvider: "Entegratörde",
  ProviderAccepted: "GİB'e iletiliyor",
  GibAccepted: "GİB'e ulaştı",
  Delivered: "Alıcıya ulaştı",
  Failed: "Başarısız",
  MatbuPending: "Matbu · dönüştürülmeli",
};
export const DESPATCH_STATE_TONE: Record<DespatchState, "ok" | "info" | "warn" | "err" | "muted"> = {
  Queued: "warn",
  SentToProvider: "info",
  ProviderAccepted: "info",
  GibAccepted: "ok",
  Delivered: "ok",
  Failed: "err",
  MatbuPending: "warn",
};
export const RA_LABEL: Record<ReceiptAdviceState, string> = {
  NotExpected: "Yanıt beklenmez",
  Pending: "Yanıt bekleniyor",
  Accepted: "Kabul edildi",
  PartiallyAccepted: "Kısmen kabul",
  Rejected: "Reddedildi",
  DeemedAccepted: "Kabul sayıldı (7 gün)",
};
export const RA_TONE: Record<ReceiptAdviceState, "ok" | "info" | "warn" | "err" | "muted"> = {
  NotExpected: "muted",
  Pending: "info",
  Accepted: "ok",
  PartiallyAccepted: "warn",
  Rejected: "err",
  DeemedAccepted: "ok",
};
export const DESPATCH_OUTCOME_LABEL: Record<DespatchAttemptOutcome, string> = {
  [DespatchAttemptOutcome.Pending]: "Gönderiliyor",
  [DespatchAttemptOutcome.Success]: "Düzenlendi",
  [DespatchAttemptOutcome.Rejected]: "Reddedildi",
  [DespatchAttemptOutcome.Failed]: "Gönderilemedi",
  [DespatchAttemptOutcome.Unknown]: "Sonuç belirsiz",
};
export const DESPATCH_OUTCOME_TONE: Record<DespatchAttemptOutcome, "ok" | "info" | "warn" | "err" | "muted"> = {
  [DespatchAttemptOutcome.Pending]: "warn",
  [DespatchAttemptOutcome.Success]: "ok",
  [DespatchAttemptOutcome.Rejected]: "err",
  [DespatchAttemptOutcome.Failed]: "err",
  [DespatchAttemptOutcome.Unknown]: "warn",
};
export const DESPATCH_KIND_LABEL: Record<DespatchAttemptKind, string> = {
  [DespatchAttemptKind.Ship]: "Sevk et",
  [DespatchAttemptKind.Resend]: "Yeniden gönder",
  [DespatchAttemptKind.Reissue]: "Yeni e-İrsaliye",
  [DespatchAttemptKind.Matbudan]: "Matbudan dönüştürme",
};

/** Sunucunun saklı yanıt özeti (Shipment.ReceiptAdviceSummaryJson). */
export interface AdviceSummary {
  state?: ReceiptAdviceState | null;
  dueDate?: string | null;
  number?: string | null;
  decision?: string | null;
  issueDate?: string | null;
  issueTime?: string | null;
  isLate?: boolean | null;
  deemedLocally?: boolean;
  lines?: { lineNo: number; shipmentLineId: string; name: string; delivered: number; accepted: number; rejected: number; reason?: string | null }[] | null;
  warnings?: { code?: string | null; message?: string | null }[];
}
export const parseAdviceSummary = (json?: string | null): AdviceSummary | null => {
  if (!json) return null;
  try { return JSON.parse(json) as AdviceSummary; } catch { return null; }
};

/** Plaka: boşluksuz, Latin büyük harf (sunucu DespatchRules.NormalizePlate ile aynı). */
export const normalizePlate = (s: string) =>
  (s || "").replace(/\s+/g, "").replace(/[ıİ]/g, "I").replace(/[şŞ]/g, "S").replace(/[ğĞ]/g, "G").replace(/[üÜ]/g, "U").replace(/[öÖ]/g, "O").replace(/[çÇ]/g, "C").toUpperCase();
/** GİB plaka şeması: Türk plakası 01–81 il koduyla başlar (34ABC123); yabancı plaka harf/rakam/tire/alt çizgi. */
export const plateValid = (plate: string, foreign: boolean) => {
  const p = normalizePlate(plate);
  if (!p || p.length > 20) return false;
  return foreign ? /^[A-Z0-9_-]+$/.test(p) : /^(0[1-9]|[1-7][0-9]|8[01])[A-Z]+[0-9]+$/.test(p);
};
/** Teslimat posta kodu: 5 hane, 01–81 il koduyla başlar (GİB şematronu DespatchAddressCheck). */
export const postalValid = (s: string) => /^((0[1-9])|([1-7][0-9])|(8[0-1]))[0-9]{3}$/.test((s || "").trim());
/** Şoför TCKN: sağlamalı, 11111111111 olamaz. */
export const driverTcknValid = (s: string) => s !== "11111111111" && isValidTckn(s);
/** Taşıyıcı VKN (10) ya da TCKN (11), sağlamalı. */
export const taxIdValid = (s: string) => {
  const t = (s || "").trim();
  return t.length === 10 ? isValidVkn(t) : t.length === 11 ? isValidTckn(t) && t !== "11111111111" : false;
};

/** e-İrsaliye bu sevkiyatın iptalini engelliyor mu (sunucu CancelAsync ile aynı kural, Kılavuz §14). */
export const despatchBlocksCancel = (d: { despatchDocumentId?: string | null; despatchState?: DespatchState | null; receiptAdviceState?: ReceiptAdviceState | null }) =>
  d.despatchState === "MatbuPending" || (!!d.despatchDocumentId && d.despatchState !== "Failed" && d.receiptAdviceState !== "Rejected");

/** e-İrsaliye hata gövdesi (ön kontrol / ret / gönderilemedi / belirsiz) — yoksa null. */
export const despatchError = (e: any): DespatchErrorBody | null => {
  const d = e?.response?.data;
  return d && typeof d === "object" && (d.outcome || d.code === "DESPATCH_PRECHECK") ? (d as DespatchErrorBody) : null;
};

/** NotificationScanner.UndeliveredDays = 3 ile aynı tutulur (ilk uyarı 3. gün, sonra 7). */
export const SH_UNDELIVERED_DAYS = 3;
export interface ShAge { days: number | null; state: "" | "soon" | "late"; stuck: boolean; text: string }
/** Yalnız Shipped için anlamlı. days = bugün − shippedAt (yerel gün). */
export const shAge = (shippedAt: string | null | undefined, status: S): ShAge => {
  if (status !== S.Shipped || !shippedAt) return { days: null, state: "", stuck: false, text: "" };
  const t = new Date(); t.setHours(0, 0, 0, 0);
  const days = Math.max(0, Math.round((t.getTime() - poDate(shippedAt).getTime()) / 86400000));
  return {
    days,
    stuck: days >= SH_UNDELIVERED_DAYS,
    state: days >= 7 ? "late" : days >= SH_UNDELIVERED_DAYS ? "soon" : "",
    text: days === 0 ? "bugün sevk edildi" : `${days} gündür yolda`,
  };
};
